import { useCallback, useState } from 'react';
import { Add, Delete, Edit } from '@mui/icons-material';
import { DevicePoolPanel } from './DevicePoolPanel';
import Permissions from 'src/Common/Permissions';
import { DevicePool, Pagination } from '@ekkogmbh/apisdk';
import { ColumnDef, DataTable, dataTableHighlight } from 'src/Common/Components/DataTable';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import CompartmentSelectorChip from 'src/CompartmentSelectorManagement/Components/CompartmentSelectorChip';
import ScrollableStack from 'src/Common/Components/ScrollableStack';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { useContentControl } from 'src/Common/Stores/ContentControlProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';

const ROW_MIN_HEIGHT = 64;

export const DevicePoolManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { scrollTop } = useNavigation();
  const { setPanelOpen } = useContentControl();
  const { refresh } = usePagination();

  const [, startLoading] = useRequest();

  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedPool, setSelectedPool] = useState<DevicePool>();

  const fetchPools = useCallback((pagination: Pagination) => api.getDevicePools(pagination), [api]);

  const deletePool = useCallback(() => {
    if (selectedPool === undefined) {
      return;
    }

    startLoading(
      () => api.deleteDevicePool(selectedPool.coordinate, selectedPool.name),
      () => {
        dismiss();
        refresh();
      },
    );
  }, [selectedPool]);

  const dismiss = useCallback(() => {
    setPanelOpen(false);
    setDialogOpen(false);
    setSelectedPool(undefined);
  }, []);

  const columns: ColumnDef[] = [
    {
      header: 'Name',
      body: (entry: DevicePool, searchText?: string) => (
        <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
          {searchText ? dataTableHighlight(entry.name, [searchText]) : entry.name}
        </ScrollableStack>
      ),
      filterField: 'name',
      sortField: 'DP.name',
      proportionalContent: '30%',
    },
    {
      header: 'Coordinate',
      body: (entry: DevicePool, searchText?: string) => (
        <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
          {searchText ? dataTableHighlight(entry.coordinate, [searchText]) : entry.coordinate}
        </ScrollableStack>
      ),
      filterField: 'coordinate',
      sortField: 'N.materializedPath',
      proportionalContent: '35%',
    },
    {
      header: 'Compartment-Selector',
      body: (entry: DevicePool, searchText?: string) => (
        <CompartmentSelectorChip compartmentSelector={entry.compartmentSelector} highlightText={searchText} />
      ),
      fitContent: true,
    },
    {
      header: 'Template-Filter',
      body: (entry: DevicePool, searchText?: string) => {
        const { templateFilter } = entry;
        const text = `${templateFilter.type} [${templateFilter.templateNames.join(', ')}]`;
        return (
          <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
            {searchText ? dataTableHighlight(text, [searchText]) : text}
          </ScrollableStack>
        );
      },
      proportionalContent: '35%',
    },
    {
      header: 'Sorting-Strategy',
      body: (entry: DevicePool) => entry.sortingStrategy.name,
      fitContent: true,
    },
  ];

  return (
    <ContentControl
      panel={<DevicePoolPanel devicePool={selectedPool} closeHandler={dismiss} />}
      dialog={
        <GenericDialog
          type="confirmation"
          maxWidth={'sm'}
          fullWidth={true}
          centered={true}
          open={isDialogOpen}
          title={'Delete Device Pool'}
          text={selectedPool?.name ?? ''}
          onClose={dismiss}
          onConfirm={deletePool}
        />
      }
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          tooltip={'Add Device Pool'}
          requiredPermission={Permissions.DEVICE_POOLS_WRITE}
          onClick={() => {
            dismiss();
            setSelectedPool(undefined);
            setPanelOpen(true);
          }}
        />,
      ]}
    >
      <DataTable
        actions={[
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: DevicePool) => {
              dismiss();
              setSelectedPool(entry);
              setPanelOpen(true);
              scrollTop();
            },
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: DevicePool) => {
              dismiss();
              setSelectedPool(entry);
              setDialogOpen(true);
            },
          },
        ]}
        columns={columns}
        fetchItems={fetchPools}
      />
    </ContentControl>
  );
};
